// Matching pipeline — pure functies, gedeeld tussen frontend en backend.
// Drie-staps strategie: exact match op pdfNaam → fuzzy fallback → handmatige review.
import { normalizeText } from './normalizer.js';
import { findTopMatches } from './fuzzy.js';
import { LEARNED_SKIP_REDEN } from './types.js';
import { berekenSchijven, round2 } from './schijven.js';
import { berekenGevel, type GevelConfig, type GevelResultaat } from './gevel.js';
import { sommeerExtraRegels, type ExtraRegel } from './extraRegels.js';
import { splitsFacturatie } from './facturatie.js';
import type {
  ParsedItem,
  Pricelist,
  PricelistItem,
  MatchedLine,
  MatchedSubItem,
  MatchSource,
  MeerwerkLine,
  SkippedItem,
  ReviewItem,
  MatchResult,
  Totals,
} from './types.js';

const AUTO_MATCH_THRESHOLD = 0.85;
const REVIEW_THRESHOLD = 0.55;

function exactKey(s: string): string {
  return normalizeText(s);
}

function buildExactIndex(pricelist: Pricelist): Map<string, PricelistItem> {
  const idx = new Map<string, PricelistItem>();
  for (const it of pricelist.items ?? []) {
    const k = exactKey(it.pdfNaam);
    if (!idx.has(k)) idx.set(k, it);
  }
  return idx;
}

/**
 * Contained-match: zoek of de PDF-string letterlijk een substring is van een prijslijst-naam,
 * of omgekeerd. Vangt het geval "Cedral Alterna ..." (prijslijst) ↔ "Alterna ..." (PDF) op.
 * Geeft het beste match (langste overlap) terug.
 */
function findContainedMatch(pdfWerken: string, pricelist: Pricelist): PricelistItem | null {
  const a = normalizeText(pdfWerken);
  if (a.length < 8) return null; // te kort om betrouwbaar substring-matchen
  let best: { item: PricelistItem; len: number } | null = null;
  for (const it of pricelist.items ?? []) {
    if (it.isNvt || it.naamArbeid === 'NVT') continue;
    const b = normalizeText(it.pdfNaam);
    if (b.length < 8) continue;
    // Substring-check beide richtingen
    if (b.includes(a) || a.includes(b)) {
      const overlap = Math.min(a.length, b.length);
      if (!best || overlap > best.len) best = { item: it, len: overlap };
    }
  }
  return best?.item ?? null;
}

function generateId(prefix: string): string {
  // Deterministisch genoeg voor matching-resultaten; UUIDs in DB-laag.
  const r = Math.random().toString(36).slice(2, 10);
  const t = Date.now().toString(36).slice(-4);
  return `${prefix}_${r}${t}`;
}

export interface MatchingOptions {
  /**
   * Map<normalizedPdfNaam, plItem> van eerder bevestigde handmatige matches.
   * Wordt geraadpleegd na de exact-match maar nog vóór contained/fuzzy zodat
   * recurrent voorkomende verschillen ("Cedral Alterna" ↔ "Alterna") meteen
   * worden gemapt zonder de gebruiker opnieuw te vragen.
   */
  learnedMatches?: Map<string, PricelistItem>;
  /**
   * Set<normalizedPdfNaam> van items die de gebruiker eerder expliciet negeerde
   * voor deze prijslijst. Wordt ná exact-match maar vóór learnedMatches
   * geraadpleegd; het item belandt bij de overgeslagen items i.p.v. in de
   * review-queue.
   */
  learnedSkips?: Set<string>;
}

/**
 * Zet de learning-rows van de API om naar MatchingOptions voor runMatching.
 * Gedeeld tussen server (/parse) en client (hermatchen, ploeg-wissel) zodat
 * beide kanten exact hetzelfde geheugen raadplegen.
 * Uitgezette regels (actief === false) worden niet toegepast (SPEC_calculatie 10.2).
 */
export function learningToMatchingOptions(
  learning: {
    matches: Array<{ pdfNaamNormalized: string; plItemId: string; actief?: boolean }>;
    skips: Array<{ pdfNaamNormalized: string; actief?: boolean }>;
  } | null | undefined,
  pricelist: Pricelist,
): MatchingOptions {
  if (!learning) return {};
  const byId = new Map<string, PricelistItem>();
  for (const it of pricelist.items ?? []) byId.set(it.id, it);
  const learnedMatches = new Map<string, PricelistItem>();
  for (const m of learning.matches) {
    if (m.actief === false) continue;
    const it = byId.get(m.plItemId);
    if (it) learnedMatches.set(m.pdfNaamNormalized, it);
  }
  const learnedSkips = new Set<string>(learning.skips.filter((s) => s.actief !== false).map((s) => s.pdfNaamNormalized));
  return { learnedMatches, learnedSkips };
}

export function runMatching(
  parsedItems: ParsedItem[],
  pricelist: Pricelist,
  options: MatchingOptions = {},
): MatchResult {
  const exactIdx = buildExactIndex(pricelist);
  const allItems = pricelist.items ?? [];
  const relevant = allItems.filter((it) => !it.isNvt && it.naamArbeid !== 'NVT');
  const nvtItems = allItems.filter((it) => it.isNvt || it.naamArbeid === 'NVT');
  const learned = options.learnedMatches ?? new Map<string, PricelistItem>();
  const learnedSkips = options.learnedSkips ?? new Set<string>();

  const groupsByArbeid = new Map<string, Array<{
    parsedItem: ParsedItem;
    plItem: PricelistItem;
    autoMatched?: boolean;
    confidence?: number;
    matchSource: MatchSource;
  }>>();
  const meerwerk: Array<{ parsedItem: ParsedItem; plItem?: PricelistItem }> = [];
  const skipped: SkippedItem[] = [];
  const reviewItems: ReviewItem[] = [];
  const unmatchedItems: ReviewItem[] = [];

  for (const pi of parsedItems) {
    const isMeerwerkCat = (pi.categorie || '').toUpperCase().includes('MEERWERK');
    const exactHit = exactIdx.get(exactKey(pi.werken));

    if (isMeerwerkCat) {
      meerwerk.push({ parsedItem: pi, plItem: exactHit });
      continue;
    }

    if (exactHit) {
      if (exactHit.isNvt || exactHit.naamArbeid === 'NVT' || exactHit.prijs === 0) {
        skipped.push({
          parsedItemId: pi.id,
          pdfNaam: pi.werken,
          reden: 'NVT — niet door onderaannemer',
        });
        continue;
      }
      const key = exactHit.naamArbeid;
      if (!groupsByArbeid.has(key)) groupsByArbeid.set(key, []);
      groupsByArbeid.get(key)!.push({ parsedItem: pi, plItem: exactHit, matchSource: 'exact' });
      continue;
    }

    const learnKey = exactKey(pi.werken);

    // Geleerde skip — de gebruiker negeerde exact deze regel al eerder voor deze prijslijst.
    if (learnedSkips.has(learnKey)) {
      skipped.push({
        parsedItemId: pi.id,
        pdfNaam: pi.werken,
        reden: LEARNED_SKIP_REDEN,
      });
      continue;
    }

    // Geleerde match — eerder handmatig bevestigd door de gebruiker.
    const learnedHit = learned.get(learnKey);
    if (learnedHit && !learnedHit.isNvt && learnedHit.naamArbeid !== 'NVT') {
      const key = learnedHit.naamArbeid;
      if (!groupsByArbeid.has(key)) groupsByArbeid.set(key, []);
      groupsByArbeid.get(key)!.push({
        parsedItem: pi,
        plItem: learnedHit,
        autoMatched: true,
        confidence: 1.0,
        matchSource: 'learned',
      });
      continue;
    }

    // Contained-match: vangt merknaam-prefix verschillen op (bv. "Cedral Alterna" ↔ "Alterna")
    const contained = findContainedMatch(pi.werken, pricelist);
    if (contained) {
      const key = contained.naamArbeid;
      if (!groupsByArbeid.has(key)) groupsByArbeid.set(key, []);
      groupsByArbeid.get(key)!.push({
        parsedItem: pi,
        plItem: contained,
        autoMatched: true,
        confidence: 0.95,
        matchSource: 'contained',
      });
      continue;
    }

    // Fuzzy fallback op relevante items
    const relTops = findTopMatches({ werken: pi.werken, eenheid: pi.eenheid }, relevant, 3);
    const best = relTops[0];

    // Check fuzzy NVT-match — als sterker, item skippen
    const nvtTop = findTopMatches({ werken: pi.werken, eenheid: pi.eenheid }, nvtItems, 1)[0];

    if (best && best.score >= AUTO_MATCH_THRESHOLD && (!nvtTop || best.score >= nvtTop.score)) {
      const plItem = best.item;
      const key = plItem.naamArbeid;
      if (!groupsByArbeid.has(key)) groupsByArbeid.set(key, []);
      groupsByArbeid.get(key)!.push({
        parsedItem: pi,
        plItem,
        autoMatched: true,
        confidence: best.score,
        matchSource: 'fuzzy',
      });
    } else if (nvtTop && nvtTop.score >= AUTO_MATCH_THRESHOLD) {
      skipped.push({
        parsedItemId: pi.id,
        pdfNaam: pi.werken,
        reden: `NVT — fuzzy match met "${nvtTop.item.pdfNaam}" (${(nvtTop.score * 100).toFixed(0)}%)`,
      });
    } else if (best && best.score >= REVIEW_THRESHOLD) {
      reviewItems.push({
        parsedItem: pi,
        suggestions: relTops.map((t) => ({ item: t.item, score: t.score })),
      });
    } else {
      unmatchedItems.push({
        parsedItem: pi,
        suggestions: relTops.map((t) => ({ item: t.item, score: t.score })),
      });
    }
  }

  // Bouw matched lines per naamArbeid
  const matchedLines: MatchedLine[] = [];
  for (const [naamArbeid, members] of groupsByArbeid.entries()) {
    let subtotaal = 0;
    let totHoeveelheid = 0;
    const subItems: MatchedSubItem[] = [];
    const eenheid = members[0]!.plItem.eenheid;
    let priceUniform = true;
    const firstPrice = members[0]!.plItem.prijs;

    for (const m of members) {
      const hv = m.parsedItem.werkelijkeHoeveelheid || 0;
      const prijs = m.plItem.prijs || 0;
      subtotaal += hv * prijs;
      totHoeveelheid += hv;
      if (prijs !== firstPrice) priceUniform = false;
      subItems.push({
        parsedItemId: m.parsedItem.id,
        pdfNaam: m.parsedItem.werken,
        plItemId: m.plItem.id,
        hoeveelheid: hv,
        eenheid: m.plItem.eenheid,
        prijs,
        subtotaal: hv * prijs,
        confidence: m.confidence ?? 1.0,
        autoMatched: m.autoMatched ?? false,
        matchSource: m.matchSource,
      });
    }

    const line: MatchedLine = {
      id: generateId('ml'),
      naamArbeid,
      eenheid,
      prijs: priceUniform ? firstPrice : null,
      hoeveelheid: totHoeveelheid,
      subtotaal,
      consumedParsedItemIds: members.map((m) => m.parsedItem.id),
      autoMatched: true,
      subItems,
    };
    // Post met schijven (bv. zonnepanelen Nedelco): snapshot van de schijven op de lijn,
    // het subtotaal volgt uit de ladder over de totale hoeveelheid.
    const schijven = members[0]!.plItem.schijven;
    if (schijven) {
      line.schijven = schijven;
      line.prijs = null;
      line.subtotaal = berekenSchijven(totHoeveelheid, schijven).totaal;
    }
    matchedLines.push(line);
  }

  // Volgorde van de matched lines volgt de opstartfiche: sorteer op de eerste positie
  // van een geconsumeerde parsedItem in de oorspronkelijke parsedItems-lijst.
  const orderById = new Map<string, number>();
  parsedItems.forEach((pi, idx) => orderById.set(pi.id, idx));
  const firstOrder = (line: MatchedLine): number => {
    let min = Number.POSITIVE_INFINITY;
    for (const pid of line.consumedParsedItemIds) {
      const o = orderById.get(pid);
      if (o != null && o < min) min = o;
    }
    return min === Number.POSITIVE_INFINITY ? Number.MAX_SAFE_INTEGER : min;
  };
  matchedLines.sort((a, b) => firstOrder(a) - firstOrder(b));

  const meerwerkLines: MeerwerkLine[] = meerwerk.map((m) => {
    const isUsableHit = m.plItem && !m.plItem.isNvt && m.plItem.naamArbeid !== 'NVT';
    const prijs = isUsableHit ? m.plItem!.prijs || 0 : 0;
    const hoeveelheid = m.parsedItem.werkelijkeHoeveelheid || 0;
    return {
      id: generateId('mw'),
      pdfNaam: m.parsedItem.werken,
      naamArbeid: isUsableHit ? m.plItem!.naamArbeid : 'MEERWERK',
      categorie: m.parsedItem.categorie,
      eenheid: isUsableHit ? m.plItem!.eenheid : m.parsedItem.eenheid || '',
      hoeveelheid,
      prijs,
      subtotaal: prijs * hoeveelheid,
      parsedItemId: m.parsedItem.id,
      plItemId: m.plItem?.id,
    };
  });

  return { matchedLines, meerwerkLines, skippedItems: skipped, reviewItems, unmatchedItems };
}

import type { ChargeLine, ChargeLineCategorie } from './types.js';

/**
 * Subtotaal van één werkpost. Bij een post met schijven rekent de ladder over de totale
 * hoeveelheid; een post met een open of geweigerde overlap telt niet mee (5.4).
 */
export function lineSubtotaal(line: MatchedLine): number {
  if (line.overlap && line.overlap.status !== 'bevestigd') return 0;
  if (line.eenheidVraag?.status === 'open') return 0;
  if (line.schijven) return berekenSchijven(Math.max(0, line.hoeveelheid || 0), line.schijven).totaal;
  return line.subtotaal || 0;
}

/**
 * Herrekent de bedragen van de lijnen uit hun hoeveelheden en eenheidsprijzen, zodat de
 * server nooit een door de client opgegeven subtotaal hoeft te geloven.
 */
export function normaliseerLijnen(
  matchedLines: MatchedLine[],
  meerwerkLines: MeerwerkLine[],
): { matchedLines: MatchedLine[]; meerwerkLines: MeerwerkLine[] } {
  const ml = matchedLines.map((l) => {
    if (!l.subItems.length) {
      if (l.schijven) return { ...l, prijs: null, subtotaal: lineSubtotaal({ ...l, overlap: undefined }) };
      // Zonder eenheidsprijs (oude "varieert"-lijnen) valt er niets te herrekenen: bedrag behouden.
      if (l.prijs == null) return l;
      return { ...l, subtotaal: round2((l.hoeveelheid || 0) * l.prijs) };
    }
    const subItems = l.subItems.map((s) => ({ ...s, subtotaal: round2((s.hoeveelheid || 0) * (s.prijs || 0)) }));
    const hoeveelheid = Math.round(subItems.reduce((acc, s) => acc + (s.hoeveelheid || 0), 0) * 10000) / 10000;
    const uniform = subItems.every((s) => s.prijs === subItems[0]!.prijs);
    const line: MatchedLine = {
      ...l,
      subItems,
      hoeveelheid,
      prijs: uniform ? subItems[0]!.prijs : null,
      subtotaal: round2(subItems.reduce((acc, s) => acc + s.subtotaal, 0)),
    };
    if (line.schijven) {
      line.prijs = null;
      line.subtotaal = berekenSchijven(Math.max(0, hoeveelheid), line.schijven).totaal;
    }
    return line;
  });
  const mw = meerwerkLines.map((m) => ({ ...m, subtotaal: round2((m.hoeveelheid || 0) * (m.prijs || 0)) }));
  return { matchedLines: ml, meerwerkLines: mw };
}

export interface TotalsInput {
  matchedLines: MatchedLine[];
  meerwerkLines: MeerwerkLine[];
  /** Nieuw datamodel (extra werken + aanrekenbare kosten). Heeft voorrang op chargeLines. */
  extraRegels?: ExtraRegel[];
  /** @deprecated oude aanrekenbare kosten — enkel als extraRegels ontbreekt. */
  chargeLines?: ChargeLine[];
  /** Snapshot van de gevelprijslijst A/B; null/undefined = geen crepi-ladder (oude calculaties). */
  gevel?: GevelConfig | null;
  /** Percentage dat nu gefactureerd mag worden (6.3, calculatie 11); null = alles. */
  factuurPercentage?: number | null;
}

export interface TotalsMetGevel extends Totals {
  gevel: GevelResultaat | null;
  /** Open voor oplevering (27okt 4): open posten + het restpercentage. Vastgelegde kost, nog niet te factureren. */
  openVoorOplevering: number;
  /** Wat de onderaannemer nu mag factureren = totaal − open voor oplevering. */
  nuFactureerbaar: number;
  /** Deel van "open voor oplevering" uit de posten die per post open staan. */
  openPosten: number;
  /** Deel van "open voor oplevering" uit het restpercentage (6.3). */
  percentageRest: number;
  factuurPercentage: number | null;
}

/**
 * Een meerwerk uit een extra offerte telt pas mee na bevestiging (SPEC_workflow 4), een werkpost met een
 * afwijkende eenheid pas na bevestiging van de hoeveelheid (SPEC_calculatie 8.1). Tot dan doen ze niet
 * mee in de totalen, de crepi-ladder of de PDF.
 */
export function meerwerkTeltMee(m: MeerwerkLine): boolean {
  return !m.extraOfferte || m.extraOfferte.status === 'bevestigd';
}

export function werkpostTeltMee(l: MatchedLine): boolean {
  return l.eenheidVraag?.status !== 'open';
}

/** De lijnen die meetellen in totalen, ladder en weergave. */
export function tellendeLijnen<T extends { matchedLines: MatchedLine[]; meerwerkLines: MeerwerkLine[] }>(input: T): T {
  return { ...input, matchedLines: input.matchedLines.filter(werkpostTeltMee), meerwerkLines: input.meerwerkLines.filter(meerwerkTeltMee) };
}

export interface OpenPost {
  /** 'percentage' = het restpercentage van de calculatie (6.3), enkel in een vrijgave. */
  soort: 'werkpost' | 'meerwerk' | 'extra_werk' | 'percentage';
  id: string;
  naam: string;
  hoeveelheid: number | null;
  eenheid: string;
  bedrag: number;
  verwachteDatum: string | null;
  opmerking: string;
}

/**
 * Posten die open staan voor oplevering (27okt 4). Posten die door de crepi-ladder geprijsd
 * worden, kunnen niet apart open staan: de ladder rekent altijd op het volledige project
 * (SPEC_calculatie 11).
 */
export function lijstOpenPosten(
  input: Pick<TotalsInput, 'matchedLines' | 'meerwerkLines' | 'extraRegels'>,
  gevel: GevelResultaat | null,
): OpenPost[] {
  input = tellendeLijnen(input);
  const vervangen = new Set(gevel?.vervangenLijnIds ?? []);
  const vervangenMw = new Set(gevel?.vervangenMeerwerkIds ?? []);
  const out: OpenPost[] = [];
  const meta = (o: { verwachteDatum?: string | null; opmerking?: string } | undefined) => ({
    verwachteDatum: o?.verwachteDatum ?? null,
    opmerking: o?.opmerking ?? '',
  });
  for (const l of input.matchedLines) {
    if (l.oplevering?.status !== 'open' || vervangen.has(l.id)) continue;
    out.push({ soort: 'werkpost', id: l.id, naam: l.naamArbeid, hoeveelheid: l.hoeveelheid, eenheid: l.eenheid, bedrag: lineSubtotaal(l), ...meta(l.oplevering) });
  }
  for (const m of input.meerwerkLines) {
    if (m.oplevering?.status !== 'open' || vervangenMw.has(m.id)) continue;
    out.push({ soort: 'meerwerk', id: m.id, naam: m.naamArbeid || m.pdfNaam, hoeveelheid: m.hoeveelheid, eenheid: m.eenheid, bedrag: round2(m.subtotaal || 0), ...meta(m.oplevering) });
  }
  for (const r of input.extraRegels ?? []) {
    if (r.soort !== 'extra_werk' || r.oplevering?.status !== 'open') continue;
    out.push({ soort: 'extra_werk', id: r.id, naam: r.omschrijving, hoeveelheid: null, eenheid: '', bedrag: round2(r.bedrag || 0), ...meta(r.oplevering ?? undefined) });
  }
  return out;
}

/**
 * Te factureren totaal van één calculatie (SPEC_wijzigingen 2.4):
 *   werkposten + meerwerken + alle extra werken − alle aanrekenbare kosten,
 * ongeacht toerekening of vlag. Werkposten bevatten de crepi-ladder als de calculatie een
 * gevelprijslijst heeft; de regels die de ladder overneemt tellen dan niet meer apart.
 */
export function computeTotals(volledig: TotalsInput): TotalsMetGevel {
  const input = tellendeLijnen(volledig);
  const gevel = input.gevel ? berekenGevel(input.matchedLines, input.meerwerkLines, input.gevel) : null;
  const vervangen = new Set(gevel?.vervangenLijnIds ?? []);
  const vervangenMw = new Set(gevel?.vervangenMeerwerkIds ?? []);

  const subtotaal = round2(
    input.matchedLines.filter((l) => !vervangen.has(l.id)).reduce((s, l) => s + lineSubtotaal(l), 0) +
      (gevel?.totaalWerkposten ?? 0),
  );
  const meerwerk = round2(
    input.meerwerkLines.filter((m) => !vervangenMw.has(m.id)).reduce((s, l) => s + (l.subtotaal || 0), 0) +
      (gevel?.totaalMeerwerk ?? 0),
  );
  let extraWerk = 0;
  let aanrekening = 0;
  if (input.extraRegels) {
    ({ extraWerk, aanrekening } = sommeerExtraRegels(input.extraRegels));
  } else {
    aanrekening = round2((input.chargeLines ?? []).reduce((s, c) => s + (c.bedrag || 0), 0));
  }
  const totaal = round2(subtotaal + meerwerk + extraWerk - aanrekening);
  const openPosten = round2(lijstOpenPosten(input, gevel).reduce((s, p) => s + p.bedrag, 0));
  const factuurPercentage = input.factuurPercentage ?? null;
  const f = splitsFacturatie({ totaal, openPosten, percentage: factuurPercentage });
  return {
    subtotaal,
    meerwerk,
    extraWerk,
    aanrekening,
    totaal,
    gevel: gevel?.actief ? gevel : null,
    openVoorOplevering: f.openVoorOplevering,
    nuFactureerbaar: f.nuFactureerbaar,
    openPosten,
    percentageRest: f.percentageRest,
    factuurPercentage,
  };
}

/** Som van aanrekenbare kosten per categorie (voor uitsplitsing in PDF/UI). */
export function chargesByCategorie(chargeLines: ChargeLine[]): Record<ChargeLineCategorie, number> {
  const out: Record<ChargeLineCategorie, number> = { SCHADE: 0, INHOUDING: 0, BOETE: 0, ANDERE: 0 };
  for (const c of chargeLines) {
    out[c.categorie] = (out[c.categorie] || 0) + (c.bedrag || 0);
  }
  return out;
}

/** Mens-leesbare labels voor charge categorieën. */
export const CHARGE_LABELS: Record<ChargeLineCategorie, string> = {
  SCHADE: 'Schade',
  INHOUDING: 'Inhouding',
  BOETE: 'Boete',
  ANDERE: 'Andere',
};
